import React, { useEffect, useRef, useState } from "react";
import { AdminShell } from "../components/AdminShell";
import { api, imageUrl } from "../lib/api";
import { toast } from "sonner";
import { Star, Trash2, Plus, Save, MessageSquare, Eye, EyeOff, Camera, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const EMPTY = { author_name: "", rating: 5, content: "", location: "", is_published: true, order: 0 };

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRef = useRef();
  const [pendingPhotoReviewId, setPendingPhotoReviewId] = useState(null);

  const triggerPhotoUpload = (reviewId) => {
    setPendingPhotoReviewId(reviewId);
    fileRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    const targetId = pendingPhotoReviewId;
    setPendingPhotoReviewId(null);
    if (!file || !targetId) return;
    setUploadingId(targetId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/reviews/${targetId}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto caricata");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload foto");
    } finally {
      setUploadingId(null);
    }
  };

  const removePhoto = async (reviewId) => {
    try {
      await api.delete(`/reviews/${reviewId}/photo`);
      toast.success("Foto rimossa");
      load();
    } catch { toast.error("Errore rimozione foto"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reviews/all");
      setReviews(r.data);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      author_name: r.author_name || "",
      rating: r.rating || 5,
      content: r.content || "",
      location: r.location || "",
      is_published: r.is_published !== false,
      order: r.order || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.author_name.trim() || !form.content.trim()) {
      toast.error("Compila autore e testo");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        author_name: form.author_name.trim(),
        rating: parseInt(form.rating) || 5,
        content: form.content.trim(),
        location: form.location.trim() || null,
        is_published: !!form.is_published,
        order: parseInt(form.order) || 0,
      };
      if (editingId) {
        await api.patch(`/reviews/${editingId}`, payload);
        toast.success("Recensione aggiornata");
      } else {
        await api.post("/reviews", payload);
        toast.success("Recensione aggiunta");
      }
      setForm(EMPTY); setEditingId(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const togglePublished = async (r) => {
    try {
      await api.patch(`/reviews/${r.id}`, { is_published: !r.is_published });
      load();
    } catch { toast.error("Errore"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      toast.success("Recensione eliminata");
      load();
    } catch { toast.error("Errore"); }
  };

  return (
    <AdminShell title="Recensioni">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handlePhotoChange}
        data-testid="review-photo-file-input"
      />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="reviews-page">
        <form onSubmit={save} className="lg:col-span-2 bg-midnight-light border border-white/5 p-6 space-y-4 h-fit lg:sticky lg:top-24" data-testid="review-form">
          <div className="flex items-center gap-3 mb-2">
            <MessageSquare size={18} className="text-gold" />
            <h3 className="font-display text-lg font-semibold text-gold">
              {editingId ? "Modifica recensione" : "Nuova recensione"}
            </h3>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">Autore *</Label>
            <Input
              value={form.author_name}
              onChange={(e) => setForm({ ...form, author_name: e.target.value })}
              placeholder="Es. Marco B."
              className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
              data-testid="review-author-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">Città (opzionale)</Label>
            <Input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Es. Milano"
              className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
              data-testid="review-location-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">Voto</Label>
            <div className="flex gap-1 mt-2" data-testid="review-rating-picker">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm({ ...form, rating: n })}
                  className="p-1 hover:scale-110 transition-transform"
                  data-testid={`review-star-${n}`}
                  aria-label={`Imposta voto ${n}`}
                >
                  <Star
                    size={22}
                    className={n <= form.rating ? "text-gold" : "text-white/20"}
                    fill={n <= form.rating ? "currentColor" : "none"}
                  />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">Testimonianza *</Label>
            <Textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={5}
              maxLength={500}
              placeholder="Racconta l'esperienza del cliente..."
              className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
              data-testid="review-content-input"
            />
            <p className="text-[10px] text-white/40 mt-1">Max 500 caratteri.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60">Ordine</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
                className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                data-testid="review-order-input"
              />
              <p className="text-[10px] text-white/40 mt-1">Più basso = prima.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60">Pubblicata</Label>
              <label className="mt-4 flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_published}
                  onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                  className="h-4 w-4 accent-gold"
                  data-testid="review-published-checkbox"
                />
                <span className="text-sm text-white/80">Visibile sul sito</span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={saving || !form.author_name.trim() || !form.content.trim()}
              className="btn-gold flex-1 flex items-center justify-center gap-2 py-3 text-xs uppercase tracking-widest font-semibold disabled:opacity-50"
              data-testid="review-save-btn"
            >
              {editingId ? <Save size={13} /> : <Plus size={13} />}
              {saving ? "Salvo..." : editingId ? "Aggiorna" : "Aggiungi"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-3 text-xs uppercase tracking-widest border border-white/20 hover:border-white/40 text-white/70"
                data-testid="review-cancel-edit"
              >
                Annulla
              </button>
            )}
          </div>
        </form>

        <div className="lg:col-span-3 space-y-3" data-testid="reviews-list">
          {loading ? (
            <p className="text-white/50 text-sm">Caricamento...</p>
          ) : reviews.length === 0 ? (
            <div className="bg-midnight-light border border-white/5 p-10 text-center">
              <MessageSquare size={30} className="text-white/20 mx-auto mb-3" />
              <p className="text-white/60">Nessuna recensione ancora.</p>
            </div>
          ) : reviews.map((r) => (
            <div key={r.id} className={`bg-midnight-light border p-5 ${r.is_published ? "border-white/5" : "border-orange-500/40"}`} data-testid={`admin-review-${r.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  {r.photo_path ? (
                    <div className="relative shrink-0 group/photo">
                      <img
                        src={imageUrl(r.photo_path)}
                        alt=""
                        className="w-14 h-14 rounded-full object-cover border border-gold/40"
                        data-testid={`admin-review-photo-${r.id}`}
                      />
                      <button
                        onClick={() => removePhoto(r.id)}
                        className="absolute -top-1 -right-1 p-1 bg-destructive text-white rounded-full opacity-0 group-hover/photo:opacity-100 transition-opacity"
                        title="Rimuovi foto"
                        data-testid={`admin-review-photo-remove-${r.id}`}
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => triggerPhotoUpload(r.id)}
                      disabled={uploadingId === r.id}
                      className="w-14 h-14 rounded-full border-2 border-dashed border-white/20 hover:border-gold flex items-center justify-center text-white/40 hover:text-gold shrink-0 disabled:opacity-50"
                      title="Aggiungi foto"
                      data-testid={`admin-review-photo-add-${r.id}`}
                    >
                      <Camera size={16} className={uploadingId === r.id ? "animate-pulse" : ""} />
                    </button>
                  )}
                  <div className="min-w-0">
                    <p className="font-display text-lg font-semibold text-gold">{r.author_name}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-white/50 flex-wrap">
                      {r.location && <span>{r.location}</span>}
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} size={11} className={i < r.rating ? "text-gold" : "text-white/15"} fill={i < r.rating ? "currentColor" : "none"} />
                        ))}
                      </div>
                      <span className="text-white/40">· ordine {r.order || 0}</span>
                      {!r.is_published && <span className="text-orange-400 uppercase tracking-widest text-[9px]">Bozza</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => togglePublished(r)}
                    className="p-2 hover:bg-white/10 rounded-sm"
                    data-testid={`review-toggle-${r.id}`}
                    title={r.is_published ? "Nascondi" : "Pubblica"}
                  >
                    {r.is_published ? <Eye size={13} className="text-emerald-400" /> : <EyeOff size={13} className="text-orange-400" />}
                  </button>
                  <button
                    onClick={() => startEdit(r)}
                    className="p-2 text-[10px] uppercase tracking-widest text-gold hover:bg-gold hover:text-midnight rounded-sm border border-gold/40"
                    data-testid={`review-edit-${r.id}`}
                  >
                    Modifica
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 text-destructive hover:bg-destructive hover:text-white rounded-sm" data-testid={`review-delete-${r.id}`}>
                        <Trash2 size={13} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare la recensione di {r.author_name}?</AlertDialogTitle>
                        <AlertDialogDescription>L'operazione è irreversibile.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(r.id)} className="bg-destructive text-white">Elimina</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">{r.content}</p>
            </div>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}
